export type LayoutMode = 'horizontal' | 'tree';

export interface SnapshotNode {
    id: number;
    team_id?: number | null;
    name: string;
    color?: string | null;
    leader_name?: string | null;
    parent_id?: number | null;
    members_count: number;
    overallScore?: number | null;
    extendedOverallScore?: number | null;
}

export const PILLAR_ORDER = [
    'Inclusion',
    'Advancement',
    'Compensation',
    'Representation',
    'Psychological Safety',
    'Emotional Intelligence',
];

export const getPillarKey = (name: string) =>
    name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_+$)/g, '');

export const hexToRgb = (hex?: string | null) => {
    if (!hex) return null;
    const cleaned = hex.replace('#', '').trim();
    if (cleaned.length === 3) {
        const r = parseInt(cleaned[0] + cleaned[0], 16);
        const g = parseInt(cleaned[1] + cleaned[1], 16);
        const b = parseInt(cleaned[2] + cleaned[2], 16);
        return { r, g, b };
    }
    if (cleaned.length === 6) {
        const r = parseInt(cleaned.slice(0, 2), 16);
        const g = parseInt(cleaned.slice(2, 4), 16);
        const b = parseInt(cleaned.slice(4, 6), 16);
        return { r, g, b };
    }
    return null;
};

export const isColorDark = (hex?: string | null) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return false;
    const lum = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
    return lum < 0.55;
};

export const lightenHex = (hex?: string | null, factor = 0.6) => {
    if (!hex) return hex;
    const rgb = hexToRgb(hex);
    if (!rgb) return hex;
    const r = Math.round(rgb.r * factor + 255 * (1 - factor));
    const g = Math.round(rgb.g * factor + 255 * (1 - factor));
    const b = Math.round(rgb.b * factor + 255 * (1 - factor));
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const darkenHex = (hex?: string | null, factor = 0.6) => {
    if (!hex) return hex;
    const rgb = hexToRgb(hex);
    if (!rgb) return hex;
    const r = Math.round(rgb.r * factor);
    const g = Math.round(rgb.g * factor);
    const b = Math.round(rgb.b * factor);
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const SCORE_LOW_THRESHOLD = 6;
export const SCORE_MEDIUM_THRESHOLD = 7.5;
export const SCORE_LOW_COLOR = 'rgba(255, 99, 132, 0.6)';
export const SCORE_MEDIUM_COLOR = 'rgba(255, 216, 111, 1)';
export const SCORE_HIGH_COLOR = 'rgba(129, 199, 132, 1)';

export const scoreToColor = (score?: number | null) => {
    if (score == null || Number.isNaN(score)) return '#d1d5db';
    const s = Math.max(0, Math.min(10, score));
    if (s < SCORE_LOW_THRESHOLD) return SCORE_LOW_COLOR;
    if (s < SCORE_MEDIUM_THRESHOLD) return SCORE_MEDIUM_COLOR;
    return SCORE_HIGH_COLOR;
};

export const getSentimentSymbol = (score: number | undefined) => {
  if (typeof score !== 'number' || Number.isNaN(score)) {
    return '•';
  }

  if (score < SCORE_LOW_THRESHOLD) {
    return '-';
  }

  if (score < SCORE_MEDIUM_THRESHOLD) {
    return '•';
  }

  return '+';
};

export const buildPillarCardItems = (
    metrics: any[],
    themeData: any[] = [],
    sentimentData: any[] = [],
    teamId?: number | null,
    pillarData?: Record<string, any>
) => {
    const metricMap = new Map<string, any>();

    metrics.forEach((m: any) => {
        const label = (m.metric_label ?? m.metric ?? '').toString().trim();
        if (!label) return;
        metricMap.set(label.toLowerCase(), m);
    });

    const normalize = (value: any) => (value ?? '').toString().trim().toLowerCase();

    const themeRows: any[] = Array.isArray(themeData)
        ? themeData.flatMap((entry: any) => Array.isArray(entry?.result?.themes) ? entry.result.themes : [])
        : [];

    const teamThemeRows = teamId != null
        ? themeRows.filter((row: any) => row.team_id != null && Number(row.team_id) === Number(teamId))
        : themeRows;

    const themeRowMap = new Map<string, any>();
    teamThemeRows.forEach((row: any) => {
        const pillarName = normalize(row.pillar_name ?? row.pillar ?? row.pillar_name);
        if (!pillarName) return;
        themeRowMap.set(pillarName, row);
    });

    const pillarDataMap = new Map<string, any>();
    if (pillarData) {
        Object.values(pillarData).forEach((pillar: any) => {
            const key = normalize(pillar.name ?? pillar.key);
            if (!key) return;
            pillarDataMap.set(key, pillar);
        });
    }

    const sentimentRows = Array.isArray(sentimentData)
        ? sentimentData.flatMap((row: any) => {
            const source = row?.result ?? row;
            return Array.isArray(source.team_pillar_sentiments) ? source.team_pillar_sentiments : [];
        })
        : [];
    const filteredSentiments = sentimentRows.filter((row: any) => teamId != null && row.team_id != null && Number(row.team_id) === Number(teamId));
    const sentimentMap = new Map<string, any>();
    filteredSentiments.forEach((row: any) => {
        const key = normalize(row.pillar_name ?? row.pillar ?? row.pillar_name);
        if (!key) return;
        if (!sentimentMap.has(key)) sentimentMap.set(key, row);
    });

    return PILLAR_ORDER.reduce((items: any[], name) => {
        const normalized = name.toLowerCase();
        const pillarRow = pillarDataMap.get(normalized);
        if (!pillarRow) return items;

        const metric = metricMap.get(normalized);
        const value = metric?.value;
        const score = typeof value === 'number' && !Number.isNaN(value)
            ? Number(value)
            : Number.isFinite(Number(value))
                ? Number(value)
                : 0;

        const themeRow = themeRowMap.get(normalized);
        const themes = Array.isArray(themeRow?.themes)
            ? themeRow.themes.slice(0, 5).map((t: any) => ({
                key: normalize(t.theme_title || t.label),
                label: t.theme_title || t.label || '',
                count: typeof t.feedback_count === 'number' ? t.feedback_count : 0,
                sentiment: typeof t.strength === 'number' ? t.strength : typeof t.sentiment === 'number' ? t.sentiment : 0,
                description: t.theme_description ?? t.description,
                signal_strength: typeof t.signal_strength === 'number' ? t.signal_strength : undefined,
            }))
            : [];

        const sentimentMatch = sentimentMap.get(normalized);
        const sentiment_score = sentimentMatch && typeof sentimentMatch.sentiment_score === 'number'
            ? Math.max(0, Math.min(10, Number(sentimentMatch.sentiment_score)))
            : undefined;
        const summary = Array.isArray(themes) && themes.length > 0
            ? themes.map((t: any) => t.label).join(' • ')
            : undefined;
        const org_avg = typeof pillarRow.org_avg === 'number' ? pillarRow.org_avg : 0;
        const qual_priority = typeof pillarRow.qual_priority === 'number' ? pillarRow.qual_priority : 0;

        items.push({
            key: getPillarKey(name),
            name,
            score,
            org_avg,
            trend: 0,
            sentiment_score,
            qual_priority,
            themes,
            recommendations: [],
            summary,
        });

        return items;
    }, [] as any[]);
};

export const getTeamAiSummary = (aiOverviewData: any, teamId?: number | null) => {
    if (!Array.isArray(aiOverviewData) || teamId == null) return null;
    const result = aiOverviewData[0]?.result || {};
    const summaries = Array.isArray(result.summaries) ? result.summaries : [];
    const found = summaries.find((summary: any) => Number(summary.team_id) === Number(teamId));
    if (!found) return null;
    return {
        summary: typeof found.summary === 'string' ? found.summary : undefined,
        bullets: Array.isArray(found.bullets) ? found.bullets : [],
    };
};

export const buildTree = (snapshots: SnapshotNode[]) => {
    const byTeamId = new Map<number, SnapshotNode>();
    snapshots.forEach(s => {
        if (s.team_id != null) byTeamId.set(s.team_id, s);
    });

    const children = new Map<number, SnapshotNode[]>();
    const roots: SnapshotNode[] = [];
    const independent: SnapshotNode[] = [];

    snapshots.forEach(s => {
        if (s.parent_id === s.team_id) {
            roots.push(s);
        } else if (s.parent_id == null) {
            independent.push(s);
        } else {
            const parentSnapshot = byTeamId.get(s.parent_id);
            if (parentSnapshot) {
                const arr = children.get(parentSnapshot.id) || [];
                arr.push(s);
                children.set(parentSnapshot.id, arr);
            } else {
                independent.push(s);
            }
        }
    });

    children.forEach((arr, key) => {
        arr.sort((a, b) => a.name.localeCompare(b.name));
        children.set(key, arr);
    });

    roots.sort((a, b) => a.name.localeCompare(b.name));
    independent.sort((a, b) => a.name.localeCompare(b.name));

    return { roots, children, independent };
};
